import type { LocalizedDict } from '../index'

// Seller draft page → send to Etsy: confirm dialog + result screen
// (features/seller/etsy/EtsyPublishPanel.tsx). Mockup approved 2026-10-27.

export type EtsyPublishStrings = {
  confirm: {
    title: string
    store: string
    options: string
    optionsValue: (rows: number, onSale: number) => string
    priceRange: string
    photos: string
    state: string
    stateValue: string
    note: string
    cancel: string
    send: string
    sending: string
    failed: string
  }
  running: { title: string; body: string }
  ok: { title: string; body: string }
  partial: { title: string; body: string }
  uncertain: {
    title: string
    body: string
    openDrafts: string
    linkTitle: string
    linkBody: string
    linkPlaceholder: string
    link: string
    noneTitle: string
    noneBody: string
    none: string
  }
  rejected: { title: string; body: string; back: string }
  openOnEtsy: string
  retry: string
  listingNo: (id: string) => string
  steps: Record<string, string>
  stepDetail: {
    listing: (tags: number, materials: number) => string
    photosOk: string
    photoFailed: (rank: number, label: string | null, message: string) => string
    options: (rows: number, onSale: number, hidden: number) => string
    variationMissing: (values: string[]) => string
    personalization: (n: number) => string
    binding: string
    skipped: string
  }
  locked: string
  checklist: {
    title: string
    hint: string
    production_partner: string
    production_partner_hint: string
    hs_code: (code: string) => string
    copy: string
    copied: string
    ai: string
    ai_hint: (n: number) => string
    gpsr: string
    publish: string
    publish_hint: string
  }
  actionFailed: string
}

export const etsyPublishI18n: LocalizedDict<EtsyPublishStrings> = {
  en: {
    confirm: {
      title: 'Send to Etsy as a draft?',
      store: 'Store',
      options: 'Options',
      optionsValue: (rows, onSale) => `${rows} rows · ${onSale} on sale · with our SKUs`,
      priceRange: 'Price range',
      photos: 'Photos',
      state: 'Status',
      stateValue: 'Draft — you publish it on Etsy',
      note: 'A new draft listing opens on Etsy. Nothing is charged; the listing fee is charged only when you publish on Etsy. After sending, this draft is locked.',
      cancel: 'Cancel',
      send: 'Send',
      sending: 'Sending…',
      failed: 'Couldn’t start sending',
    },
    running: {
      title: 'Sending to Etsy',
      body: 'You can leave this page; sending continues in the background and shows here when done.',
    },
    ok: {
      title: 'Your Etsy draft is ready',
      body: 'Everything was sent. Now check it on Etsy and publish; the listing fee is charged only when you publish there.',
    },
    partial: {
      title: 'The draft opened, a step is missing',
      body: '“Retry the missing steps” sends only what’s missing to the same draft; no second listing is created.',
    },
    uncertain: {
      title: 'Etsy didn’t answer — the listing may have been created',
      body: 'The request to create the listing went out but Etsy didn’t answer in time. To avoid a duplicate listing we don’t retry on our own. Please check your Etsy drafts first.',
      openDrafts: 'Open my Etsy drafts ↗',
      linkTitle: 'There is a draft on Etsy',
      linkBody: 'Paste the listing link; we finish the remaining steps (photos, options…) on that draft.',
      linkPlaceholder: 'https://www.etsy.com/listing/1234567890/…',
      link: 'Link and finish',
      noneTitle: 'There is no such draft on Etsy',
      noneBody: 'If you checked, we can send again from the start.',
      none: 'I checked, there’s no listing — send again',
    },
    rejected: {
      title: 'Etsy didn’t accept the listing — nothing was created',
      body: 'No draft opened on Etsy; fix it and send again.',
      back: 'Back to the draft',
    },
    openOnEtsy: 'Open on Etsy ↗',
    retry: 'Retry the missing steps',
    listingNo: (id) => `Etsy listing no. ${id}`,
    steps: {
      listing: 'Listing draft',
      photos: 'Photos',
      options: 'Options and prices',
      variation_photos: 'Colour photos',
      personalization: 'Personalization',
      binding: 'Check and match',
    },
    stepDetail: {
      listing: (tags, materials) => `Title, description, ${tags} tags, ${materials} materials, category, shipping and processing profile`,
      photosOk: 'In order, cover first',
      photoFailed: (rank, label, message) => `Photo ${rank}${label ? ` (${label})` : ''} wasn’t uploaded: ${message} Retrying sends only this one.`,
      options: (rows, onSale, hidden) => `${rows} rows — ${onSale} on sale, ${hidden} hidden · each with our SKU`,
      variationMissing: (values) => `Not linked yet: ${values.join(', ')} — linked automatically once the photo is uploaded.`,
      personalization: (n) => `${n} question(s)`,
      binding: 'Read back from Etsy; incoming orders will match this listing’s SKUs automatically',
      skipped: 'Nothing to send',
    },
    locked: 'This draft was sent to Etsy. Make further changes on Etsy. To send the same product to another store, use “List in my store” in the catalog again.',
    checklist: {
      title: 'Finish on Etsy',
      hint: 'Etsy doesn’t let apps write these; you do them on Etsy. What you tick is remembered on this device.',
      production_partner: 'Add Pexify as a production partner',
      production_partner_hint: 'On Etsy: the listing → Production partners → “Pexify”',
      hs_code: (code) => `Customs code ${code}`,
      copy: 'Copy',
      copied: 'Copied',
      ai: 'AI image disclosure',
      ai_hint: (n) => `${n} photo(s) were made with AI`,
      gpsr: 'Product safety (GPSR) details if you sell to the EU',
      publish: 'Review and publish',
      publish_hint: 'Etsy charges the $0.20 listing fee when you publish',
    },
    actionFailed: 'That didn’t work',
  },
  tr: {
    confirm: {
      title: 'Etsy’ye taslak olarak gönderilsin mi?',
      store: 'Mağaza',
      options: 'Seçenek',
      optionsValue: (rows, onSale) => `${rows} satır · ${onSale} satışta · bizim SKU’larımızla`,
      priceRange: 'Fiyat aralığı',
      photos: 'Fotoğraf',
      state: 'Durum',
      stateValue: 'Taslak — Etsy’de siz yayınlarsınız',
      note: 'Etsy’de yeni bir taslak ilan açılır. Ücret kesilmez; ilan ücreti yalnız Etsy’de yayınlayınca kesilir. Gönderdikten sonra bu taslak kilitlenir.',
      cancel: 'Vazgeç',
      send: 'Gönder',
      sending: 'Gönderiliyor…',
      failed: 'Gönderim başlatılamadı',
    },
    running: {
      title: 'Etsy’ye gönderiliyor',
      body: 'Bu sayfadan ayrılabilirsiniz; gönderim arka planda sürer ve bitince burada görünür.',
    },
    ok: {
      title: 'Etsy’de taslak hazır',
      body: 'Her şey gönderildi. Şimdi Etsy’de kontrol edip yayınlayın; ilan ücreti yalnız orada yayınlayınca kesilir.',
    },
    partial: {
      title: 'Taslak açıldı, bir adım eksik kaldı',
      body: '“Eksikleri yeniden dene” yalnız eksik olanı aynı taslağa gönderir; ikinci ilan açılmaz.',
    },
    uncertain: {
      title: 'Etsy cevap vermedi — ilan açılmış olabilir',
      body: 'İlanı açma isteği gitti ama Etsy zamanında cevap vermedi. Çift ilan açılmaması için otomatik olarak yeniden denemiyoruz. Lütfen önce Etsy’deki taslaklarınıza bakın.',
      openDrafts: 'Etsy taslaklarımı aç ↗',
      linkTitle: 'Etsy’de taslak var',
      linkBody: 'İlanın bağlantısını yapıştırın; kalan adımları (fotoğraflar, seçenekler…) o taslağa tamamlarız.',
      linkPlaceholder: 'https://www.etsy.com/listing/1234567890/…',
      link: 'Bağla ve tamamla',
      noneTitle: 'Etsy’de böyle bir taslak yok',
      noneBody: 'Kontrol ettiyseniz gönderimi baştan yapabiliriz.',
      none: 'Kontrol ettim, ilan yok — yeniden gönder',
    },
    rejected: {
      title: 'Etsy ilanı kabul etmedi — hiçbir şey açılmadı',
      body: 'Etsy’de taslak oluşmadı; düzeltip tekrar gönderebilirsiniz.',
      back: 'Taslağa dön ve düzelt',
    },
    openOnEtsy: 'Etsy’de aç ↗',
    retry: 'Eksikleri yeniden dene',
    listingNo: (id) => `Etsy ilan no. ${id}`,
    steps: {
      listing: 'İlan taslağı',
      photos: 'Fotoğraflar',
      options: 'Seçenekler ve fiyatlar',
      variation_photos: 'Renk fotoğrafları',
      personalization: 'Kişiselleştirme',
      binding: 'Kontrol ve eşleme',
    },
    stepDetail: {
      listing: (tags, materials) => `Başlık, açıklama, ${tags} etiket, ${materials} materyal, kategori, kargo ve işlem süresi profili`,
      photosOk: 'Sırayla, kapak ilk',
      photoFailed: (rank, label, message) => `${rank}. fotoğraf${label ? ` (${label})` : ''} yüklenemedi: ${message} Yeniden denemede yalnız bu gider.`,
      options: (rows, onSale, hidden) => `${rows} satır — ${onSale} satışta, ${hidden} gizli · her biri bizim SKU’muzla`,
      variationMissing: (values) => `Henüz bağlanmadı: ${values.join(', ')} — fotoğraf yüklenince otomatik bağlanır.`,
      personalization: (n) => `${n} soru`,
      binding: 'Etsy’den geri okundu; gelen siparişler bu ilanın SKU’larına otomatik bağlanacak',
      skipped: 'Gönderilecek bir şey yok',
    },
    locked: 'Bu taslak Etsy’ye gönderildi. Sonraki değişiklikleri Etsy’de yapın. Aynı ürünü başka mağazaya göndermek için katalogdan yeniden “Mağazama gönder” deyin.',
    checklist: {
      title: 'Etsy’de tamamla',
      hint: 'Etsy bunları dışarıdan yazdırmıyor; Etsy’de siz yaparsınız. İşaretledikleriniz bu cihazda hatırlanır.',
      production_partner: 'Pexify’ı üretim ortağı ekleyin',
      production_partner_hint: 'Etsy’de ilan → Üretim ortakları → “Pexify”',
      hs_code: (code) => `Gümrük kodu ${code}`,
      copy: 'Kopyala',
      copied: 'Kopyalandı',
      ai: 'AI görsel beyanı',
      ai_hint: (n) => `${n} fotoğraf yapay zekâ ile üretildi`,
      gpsr: 'AB satışı varsa ürün güvenliği (GPSR)',
      publish: 'Kontrol edip yayınlayın',
      publish_hint: 'Yayınlayınca Etsy 0,20 $ ilan ücreti keser',
    },
    actionFailed: 'İşlem yapılamadı',
  },
}
